import { logEvent } from '@/analytics/events';
import { CouponsListResponse } from '@/api/skippay/couponsList';
import cloudfrontLoader from '@/api/skippay/imageLoader';
import { LINKS } from '@/constants/links';
import { addSearchParams } from '@/utils/fetcher';
import Image from 'next/image';
import { useRouter } from 'next/router';

interface Props {
	data: NonNullable<CouponsListResponse>['data'][number];
	index: number;
}

const defaultImage = '/images/sections/discounts/discounts-default.png';

export function CouponSkeleton() {
	return (
		<div className="group relative flex w-full cursor-pointer flex-col overflow-hidden rounded-2xl bg-white p-2 pb-3 shadow-xs transition-shadow hover:shadow-md lg:aspect-square lg:p-5 lg:pb-2">
			<div className="coupon-shader hidden lg:block" />

			<Image
				className="relative mb-2 h-full max-h-26 min-h-26 w-full rounded-xl object-cover transition-transform group-hover:scale-105 lg:absolute lg:inset-0 lg:max-h-full lg:min-h-fit"
				width={147}
				height={100}
				src={defaultImage}
				alt=""
			/>

			<div className="relative z-20 mt-auto flex w-72 max-w-full animate-pulse flex-col gap-2 px-1 text-gray-900 lg:mb-2 lg:text-white">
				<div className="h-5 w-full rounded-sm bg-slate-200 lg:h-6 lg:opacity-60" />
				<div className="hidden h-6 w-[60%] rounded-sm bg-slate-200 lg:block lg:opacity-60" />
				<div className="mt-1 h-4 w-full rounded-sm bg-slate-200 lg:opacity-40" />
			</div>
		</div>
	);
}

export function Coupon(props: Props) {
	const { data, index } = props;
	const router = useRouter();

	const image = data.imageUrl || defaultImage;

	function logCouponSelected() {
		logEvent('select_promotion', {
			ecommerce: {
				items: [
					{
						creative_name: data.merchant.name,
						creative_slot: `search_${index + 1}`,
						location_id: data.url,
						promotion_id: 'affil_coupon',
						promotion_name: data.title,
					},
				],
			},
		});
	}

	const onClick = () => {
		logCouponSelected();
		const isDiscountsPage = router.pathname.startsWith(LINKS().discounts);

		const openUrl = addSearchParams(isDiscountsPage ? router.asPath : LINKS().discounts, {
			coupon: window.btoa(data.id),
		});

		window.open(openUrl, '_blank');
		window.location.href = data.url;
	};

	return (
		<button
			type="button"
			className="group relative flex w-full flex-col overflow-hidden rounded-2xl bg-white p-2 pb-3 text-left shadow-xs transition-shadow hover:shadow-md lg:aspect-square lg:p-5 lg:pb-2"
			onClick={onClick}
		>
			<div className="coupon-shader hidden lg:block" />

			<Image
				className="relative mb-2 h-full max-h-26 min-h-26 w-full rounded-xl object-cover transition-transform group-hover:scale-105 lg:absolute lg:inset-0 lg:max-h-full lg:min-h-fit"
				width={256}
				height={256}
				src={image}
				alt={data.title}
				loader={(p) => cloudfrontLoader({ ...p, height: image === defaultImage ? undefined : 256 })}
			/>

			{data.merchant.logoUrl && (
				<Image
					className="absolute top-14 left-1/2 z-10 max-h-12 -translate-x-1/2 -translate-y-1/2 transform object-contain lg:top-16 lg:left-16 lg:max-h-max"
					alt={data.merchant.name || ''}
					width={90}
					height={64}
					src={data.merchant.logoUrl}
				/>
			)}

			<div className="relative z-20 mt-auto px-1 text-gray-900 lg:text-white">
				<h4 className="mb-1 line-clamp-2 font-semibold lg:text-xl">
					{data.title || data.description}
				</h4>

				<p className="line-clamp-1 text-xs lg:text-sm">{data.description}</p>
			</div>
		</button>
	);
}
